import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import liff from "../../lib/liff";
import { createSystemWebPass, linkSystemLine } from "../../utils/api";

/**
 * SoulChat Web の「場所」（作中の BAR など）を持つ作品の LIFF。作品ごとの画面は持たず、
 * LINE と Web の間をつなぐだけ（画面そのものは SoulChat Web にある）。
 *
 * - `/<work>/line-link?t=...`: Web で交換した LINE を、いまの LINE に結んで友だち追加へ。
 * - `/<work>/<page>?soul_id=...`: リッチメニューの行き先。同じ相手として Web の場所へ入る。
 *   その OS で SoulChat のアプリを使っている会員には、Backend が `open: "app"` を返す。そのときは
 *   LINE の外で開き（Universal Links / App Links でアプリの BAR が開く）、LIFF を閉じる。
 */

// リッチメニューの行き先（LIFF のページ名）→ Web で開く画面。
export const WEB_DESTINATIONS = {
  bar: "chat",
  quests: "quests",
  mypage: "mypage",
  "how-to-use": "how_to_use",
  tickets: "tickets",
};

const screen = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 16,
  padding: "0 28px",
  background: "#120d0b",
  color: "#f5ebe0",
  textAlign: "center",
  fontFamily: "'Noto Sans JP', sans-serif",
};
const note = { margin: 0, fontSize: 14, fontWeight: 600, lineHeight: 1.8 };
const button = {
  minWidth: 220,
  minHeight: 48,
  border: 0,
  borderRadius: 999,
  color: "#fff",
  background: "#06c755",
  fontSize: 15,
  fontWeight: 700,
};

function open(url) {
  if (liff.isInClient?.()) {
    liff.openWindow({ url, external: false });
    return;
  }
  window.location.replace(url);
}

export function VenueWebPass() {
  const { page } = useParams();
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const soulId = new URLSearchParams(window.location.search).get("soul_id");
    const to = WEB_DESTINATIONS[page];
    if (!soulId || !to) {
      setFailed(true);
      return;
    }
    createSystemWebPass(soulId, to, liff.getOS?.())
      .then(({ url, open: opens }) => {
        if (opens === "app") {
          liff.openWindow({ url, external: true });
          liff.closeWindow?.();
          return;
        }
        window.location.replace(url);
      })
      .catch(() => setFailed(true));
  }, [page]);

  return (
    <div style={screen}>
      <p style={note}>{failed ? "開けませんでした。少し待ってから、もう一度お試しください。" : "SoulChat"}</p>
    </div>
  );
}

export function VenueLineLink() {
  const [state, setState] = useState("linking"); // linking | linked | conflict | error
  const [friendUrl, setFriendUrl] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const ticket = new URLSearchParams(window.location.search).get("t");
    if (!ticket) {
      setState("error");
      return;
    }
    linkSystemLine(ticket)
      .then(({ add_friend_url: url }) => {
        setFriendUrl(url);
        setState("linked");
        if (url) open(url);
      })
      .catch((error) => setState(error?.status === 409 ? "conflict" : "error"));
  }, []);

  return (
    <div style={screen}>
      {state === "linking" && <p style={note}>LINEとつないでいます...</p>}
      {state === "linked" && (
        <>
          <p style={note}>つながりました。友だち追加すると、LINEで話せます。</p>
          {friendUrl && (
            <button type="button" style={button} onClick={() => open(friendUrl)}>
              友だち追加する
            </button>
          )}
        </>
      )}
      {state === "conflict" && (
        <p style={note}>このLINEは、すでに別のSoulChatの記録とつながっています。</p>
      )}
      {state === "error" && (
        <p style={note}>このリンクは使えなくなりました。もう一度、BARの画面から開いてください。</p>
      )}
    </div>
  );
}
